import React from 'react';
import {HSDrug, HSPackedMedication, HSPackedPrnMedication, HSPatient} from 'server-openapi';
import {Layout} from '../../../../kit/Layout';
import {Image} from '../../../../kit/Image';
import styled from 'styled-components';
import {AdministrationWarningIcons, DrugWarningIcons} from '../medicationInformation/DrugDetails';
import {DateUtils} from '../../../../core/utils/dateUtils';
import {DrugUtils} from '../../../../syncstream/utils/DrugUtils';
import {Text} from '../../../../kit/Text';
import {checkIfMedicationIsSelfAdministered} from '../../../../syncstream/utils/PackedPatientDayUtils';
import {useApiUtils} from '../../../../syncstream/utils/hooks/useApiUtils';
import {colors} from "../../../../kit/Theme/Theme";
import {getConsentDescription} from "../../../../core/utils/consentUtils";

interface IProps {
  drug: HSDrug;
  packedMedication?: HSPackedMedication | HSPackedPrnMedication;
  facilityGroupId: number;
  patient: HSPatient;
}

export function DrugDetailLeftPanel(props: IProps) {
  const apiUtils = useApiUtils();
  const chartItemDose = apiUtils.patients.getHSChartDoseFromPackedMedication(props.patient, props.packedMedication);
  const medication = apiUtils.patients.getHSMedicationFromPackedMedication(props.patient, props.packedMedication);
  const consentDescription = getConsentDescription(medication?.psychotropicConsent, medication?.consent); 

  const getDoseTimestamp = (packedMedication: HSPackedMedication | HSPackedPrnMedication) => {
    if ('doseTimestamp' in packedMedication) {
      return packedMedication.doseTimestamp;
    }
  }

  const doseTimestamp = !!props.packedMedication ? getDoseTimestamp(props.packedMedication) : undefined;


  return (
    <Layout gap={0.5}>
      {props.drug.imageUri && <DrugImage src={props.drug.imageUri} facilityGroupId={props.facilityGroupId}/>}
      {!DrugUtils.drugHasNoWarning(props.drug) && (
        <DrugWarningsContainer>
          <DrugWarningIcons displayLabels drugWarnings={DrugUtils.getDrugWarnings(props.drug)}/>
        </DrugWarningsContainer>
      )}
      <DrugDetailText weight={'bold'} size="medium">
        {!!doseTimestamp && DateUtils.dateStringTo24HourTimeString(doseTimestamp)}{props.drug.name}{' '}
        {props.drug.formAbbreviation}&emsp;{props.drug.strength}
      </DrugDetailText>
            <DrugDetailText>{props.drug.genericName ? `(${props.drug.genericName})` : ''}</DrugDetailText><Line></Line>
      <DrugDetailText>
        {props.packedMedication?.directions}{' '}
        {props.packedMedication?.route?.code && `: ${props.packedMedication?.route?.code}`}
      </DrugDetailText>
      {chartItemDose?.reasonForPrescribing && (
        <DrugDetailText>Reason for prescribing: {chartItemDose?.reasonForPrescribing}</DrugDetailText>
      )}
      {chartItemDose?.indications && <DrugDetailText>Indications: {chartItemDose?.indications}</DrugDetailText>}
      {consentDescription && <DrugDetailText>{consentDescription}</DrugDetailText>}

      <Layout horizontal>
        <AdministrationWarningIcons
          administrationWarnings={DrugUtils.getAdministrationWarnings(
            props.drug,
            props.packedMedication,
            apiUtils.patients.getHSMedicationFromPackedMedication(props.patient, props.packedMedication),
            props.packedMedication ? checkIfMedicationIsSelfAdministered(props.packedMedication, props.patient) : false,
          )}
        />
      </Layout>
    </Layout>
  );
}

const DrugDetailText = styled(Text)`
    color: ${(p) => p.theme.backgrounds.default.fg};
`;

const DrugImage = styled(Image)`
    object-fit: cover;
    max-width: 60%;
    height: 100px;
    margin-bottom: 2em;
  border-radius: 3px;
`;

const DrugWarningsContainer = styled(Layout)`
    background: ${(p) => p.theme.backgrounds.lighter.bg};
  padding: .5em;
  border-radius: 3px;
    width: fit-content;
`;

const Line = styled.hr`
    width: 50%;
    margin: .5em 0;
    border: 1px solid ${colors.bg_green_400};
`;

